import React, { useState, useEffect, useRef } from 'react';
import { 
  Globe2, 
  ShieldCheck, 
  ArrowUp, 
  Phone, 
  Mail, 
  MapPin, 
  FileCode, 
  ExternalLink,
  Code2,
  Heart,
  Navigation
} from 'lucide-react';
import { Language, AppPage } from '../types';
import { translations } from '../data/translations';
import { SorenaLogo } from './SorenaLogo';

interface FooterProps {
  lang: Language;
  onNavigate?: (page: AppPage) => void;
}

export const Footer: React.FC<FooterProps> = ({ lang, onNavigate }) => {
  const [tehranTime, setTehranTime] = useState<string>('');
  const [showInteractiveMap, setShowInteractiveMap] = useState<boolean>(false);
  const t = translations[lang];

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Asia/Tehran',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      setTehranTime(now.toLocaleTimeString(lang === 'fa' ? 'fa-IR' : 'en-US', options));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [lang]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer id="main-footer" className="bg-[#0a0503] text-stone-400 border-t border-amber-900/30 pt-16 pb-12 relative overflow-hidden">
      {/* Subtle bottom glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-amber-600/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 pb-12 border-b border-amber-900/30">
          
          {/* Brand Col (1-4) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 min-w-[44px] min-h-[44px] shrink-0 rounded-xl bg-gradient-to-tr from-amber-400 via-yellow-400 to-amber-600 p-[1.5px] shadow-lg shadow-amber-500/20">
                <div className="w-full h-full bg-[#0c0704] rounded-[10px] p-1.5 flex items-center justify-center overflow-hidden">
                  <SorenaLogo className="w-full h-full min-w-[28px] min-h-[28px]" glow />
                </div>
              </div>
              <div>
                <span className="font-extrabold text-white text-lg block">
                  {lang === 'fa' ? 'شرکت مهندسی سورنا آی‌تی' : 'Sorena IT Engineering'}
                </span>
                <span className="text-xs text-amber-400 font-mono">sorena-it.ir</span>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-stone-400 leading-relaxed">
              {t.footer.about}
            </p>

            {/* Live Iran Time & Status */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-stone-950/80 border border-amber-900/40 text-xs backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              <span className="text-stone-300">
                {lang === 'fa' ? 'ساعت رسمی تهران:' : 'Tehran Time (GMT+3:30):'}
              </span>
              <span className="font-mono text-amber-300 font-bold">{tehranTime}</span>
            </div>

            {/* Office Address */}
            <div className="flex items-start gap-2 text-xs text-stone-300 pt-1 leading-relaxed">
              <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <span>{t.contact.officeAddress}</span>
            </div>

            {/* Direct Phone & Landlines & WhatsApp in Footer */}
            <div className="flex flex-wrap gap-2 pt-1">
              <a
                href="tel:+989128634908"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#180e07] border border-amber-900/50 hover:border-amber-500/60 text-stone-300 hover:text-white text-xs transition-colors"
                title={lang === 'fa' ? 'تماس مستقیم موبایل' : 'Mobile Call'}
              >
                <Phone className="w-3.5 h-3.5 text-amber-400" />
                <span dir="ltr" className="font-mono font-bold">(+98)9128634908</span>
              </a>

              <a
                href="tel:+982633504775"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#180e07] border border-amber-900/50 hover:border-amber-500/60 text-stone-300 hover:text-white text-xs transition-colors"
                title={lang === 'fa' ? 'تلفن ثابت دفتر ۱' : 'Landline 1'}
              >
                <Phone className="w-3.5 h-3.5 text-amber-400" />
                <span dir="ltr" className="font-mono font-bold">026-33504775</span>
              </a>

              <a
                href="tel:+982633504772"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#180e07] border border-amber-900/50 hover:border-amber-500/60 text-stone-300 hover:text-white text-xs transition-colors"
                title={lang === 'fa' ? 'تلفن ثابت دفتر ۲' : 'Landline 2'}
              >
                <Phone className="w-3.5 h-3.5 text-amber-400" />
                <span dir="ltr" className="font-mono font-bold">026-33504772</span>
              </a>

              <a
                href="https://wa.me/989128634908?text=Hello%20Sorena%20IT"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#241308] border border-amber-600/50 text-amber-300 hover:bg-[#341b0b] text-xs transition-colors font-medium shadow-sm"
              >
                <span>{lang === 'fa' ? 'واتساپ:' : 'WhatsApp:'}</span>
                <span dir="ltr" className="font-mono font-bold">(+98)9128634908</span>
              </a>
            </div>
          </div>

          {/* Quick Links (5-7) */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-sm font-bold text-white tracking-wider">
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('services') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.services}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('campaign') : undefined}
                  className="hover:text-yellow-300 text-amber-400 font-semibold transition-colors cursor-pointer text-start flex items-center gap-1"
                >
                  <span>{lang === 'fa' ? 'کمپین تخفیف هرمی (۷۰٪)' : 'Pyramid Campaign'}</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('algo-trading') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {lang === 'fa' ? 'کدنویسی فارکس (MQL5)' : 'Forex Algo (MQL5)'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('portfolio') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.portfolio}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('tech-comparison') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.techComparison}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('calculator') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.calculator}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('blog') : undefined}
                  className="hover:text-yellow-300 text-amber-300 font-semibold transition-colors cursor-pointer text-start"
                >
                  {lang === 'fa' ? 'وبلاگ و مقالات سئو' : 'SEO & Tech Blog'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('why-us') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.whyUs}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('faq') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {t.nav.faq}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate ? onNavigate('contact') : undefined}
                  className="hover:text-amber-300 transition-colors cursor-pointer text-start"
                >
                  {lang === 'fa' ? 'تماس با ما و مشاوره' : 'Contact & Order'}
                </button>
              </li>
            </ul>
          </div>

          {/* Web Technologies & Services (8-10) */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-sm font-bold text-white tracking-wider">
              {t.footer.servicesLinks}
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'طراحی با React 19 و Next.js' : 'Development with React 19 & Next.js'}
                </span>
              </li>
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'طراحی قالب اختصاصی وردپرس' : 'Custom WordPress Themes & Plugins'}
                </span>
              </li>
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'پورتال سازمانی تایپو ۳ (TYPO3)' : 'TYPO3 Enterprise Portals'}
                </span>
              </li>
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'توسعه پورتال‌های جوملا ۵' : 'Joomla 5 Corporate Portals'}
                </span>
              </li>
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'فروشگاه‌های اینترنتی ووکامرس' : 'WooCommerce E-Commerce Platforms'}
                </span>
              </li>
              <li>
                <span className="text-slate-300">
                  {lang === 'fa' ? 'سئو تکنیکال و رتبه ۱ گوگل' : 'Technical SEO & Google Top Rankings'}
                </span>
              </li>
            </ul>

            {/* Live Google Map directly under item */}
            <div
              id="footer-google-map"
              className="pt-3 w-full relative z-10"
            >
              <div id="footer-google-map-container" className="rounded-xl overflow-hidden border border-amber-900/50 bg-[#0e0704] p-2.5 shadow-lg shadow-black/50 w-full">
                <div className="flex items-center justify-between mb-2 px-1 text-[11px]">
                  <div className="flex items-center gap-1.5 font-bold text-amber-400 min-w-0">
                    <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="truncate">{lang === 'fa' ? 'موقعیت دفتر: البرز، کرج، ۴۵ متری گلشهر' : 'Live Google Map'}</span>
                  </div>
                  <a
                    href="https://maps.app.goo.gl/A5UYNSdNen8KYhCm7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-amber-400 hover:text-yellow-300 transition-colors font-semibold shrink-0"
                    title={lang === 'fa' ? 'باز کردن در گوگل مپ' : 'Open in Google Maps'}
                  >
                    <span>{lang === 'fa' ? 'مسیریابی' : 'Open Map'}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="relative w-full h-44 sm:h-48 rounded-lg overflow-hidden bg-[#160c07] border border-amber-950/60 shadow-inner">
                  {showInteractiveMap ? (
                    <iframe
                      title="Google Maps Location - Surena sphere Alborz"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1000!2d50.9358138!3d35.8182496!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3f8dbe869d817f63%3A0x3934427cea743f19!2sSurena%20sphere%20Alborz!5e0!3m2!1sfa!2sir!4v1700000000000!5m2!1sfa!2sir"
                      className="w-full h-full border-0"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-b from-[#180e07] via-[#120904] to-[#0d0603] relative group select-none">
                      {/* Stylized cartographic background lines */}
                      <div className="absolute inset-0 opacity-15 bg-[radial-gradient(#f59e0b_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 pointer-events-none" />
                      
                      {/* Center Pin & Pulsing Marker */}
                      <div className="relative z-10 flex flex-col items-center text-center gap-2">
                        <div className="relative">
                          <span className="animate-ping absolute -inset-1 rounded-full bg-amber-500 opacity-60" />
                          <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-950/80 text-black">
                            <MapPin className="w-4 h-4 fill-black/20" />
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-amber-200">
                            {lang === 'fa' ? 'دفتر رسمی سورنا آی‌تی' : 'Sorena IT Headquarters'}
                          </div>
                          <div className="text-[10px] text-stone-400 font-mono mt-0.5">
                            35.8182° N, 50.9358° E • Karaj
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => setShowInteractiveMap(true)}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-[11px] transition-all shadow-md hover:shadow-amber-500/25 flex items-center gap-1.5 cursor-pointer"
                          >
                            <Navigation className="w-3 h-3" />
                            <span>{lang === 'fa' ? 'نمایش نقشه زنده' : 'Load Live Map'}</span>
                          </button>
                          <a
                            href="https://maps.app.goo.gl/A5UYNSdNen8KYhCm7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 rounded-lg bg-[#24140a] hover:bg-[#331c0e] border border-amber-800/40 text-amber-300 font-medium text-[11px] transition-colors flex items-center gap-1"
                          >
                            <span>{lang === 'fa' ? 'مسیریابی سریع' : 'Navigate'}</span>
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Compact Address & Landlines Bar below map */}
                <div className="mt-2 pt-2 border-t border-amber-950/80 flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-stone-300">
                  <div className="flex items-center gap-1 text-stone-300">
                    <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                    <span className="text-[10.5px]">
                      {lang === 'fa' 
                        ? 'البرز - کرج - ۴۵ متری گلشهر - بالای بانک اقتصاد نوین - واحد ۱' 
                        : 'Unit 1, Above Eghtesad Novin Bank, 45-Metri Golshahr, Karaj, Alborz'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 font-mono text-[11px] text-amber-300 font-bold">
                    <a href="tel:+982633504775" className="hover:text-yellow-300 transition-colors">026-33504775</a>
                    <span className="text-amber-800">/</span>
                    <a href="tel:+982633504772" className="hover:text-yellow-300 transition-colors">026-33504772</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client Trust & Direct Communication (11-12) */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-sm font-bold text-white tracking-wider">
              {lang === 'fa' ? 'پشتیبانی و گارانتی' : 'Support & SLA'}
            </h3>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center gap-1.5 text-stone-300">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span>{lang === 'fa' ? 'گارانتی تحویل و SLA ۹۹.۹٪' : '99.9% Uptime & Delivery SLA'}</span>
              </li>
              <li className="flex items-center gap-1.5 text-stone-300">
                <Code2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span>{lang === 'fa' ? 'کدنویسی استاندارد و تمیز' : 'Clean Code Standard'}</span>
              </li>
              <li className="flex items-center gap-1.5 text-stone-300">
                <Heart className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span>{lang === 'fa' ? 'مشاوره و پشتیبانی مستقیم' : 'Direct Support & Consultation'}</span>
              </li>
            </ul>

            <button
              onClick={scrollToTop}
              className="mt-4 p-2.5 rounded-xl bg-[#180e07] hover:bg-[#241308] text-stone-300 hover:text-white border border-amber-900/50 transition-colors flex items-center gap-1.5 text-xs font-semibold backdrop-blur-sm cursor-pointer"
              aria-label="Scroll to Top"
            >
              <ArrowUp className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'fa' ? 'بازگشت به بالا' : 'Back to Top'}</span>
            </button>
          </div>

        </div>

        {/* Bottom Rights Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-400 font-medium">
          <div>{t.footer.rights}</div>
        </div>

      </div>
    </footer>
  );
};
